import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core';

/**
 * Path A — acesso direto via Apollo (sem codegen), pois o endpoint do codegen
 * (click-api.mobilex.tech) está com DNS NXDOMAIN. O runtime usa
 * environment.base_graphql, que JÁ expõe estas queries (confirmado: respondem
 * UNAUTHENTICATED via GqlAuthGuard, ou seja, existem e exigem o JWT que o
 * header-interceptor já anexa).
 *
 * Esta primeira fase cobre apenas a aba "Comitês".
 */

export interface SearchIdeaCommitteeDashboardInput {
    countryIds?: string[];
    year?: number;
    month?: number;
    committeeIds?: string[];
    statuses?: string[];
    challengeIds?: string[];
    page?: number;
}

export interface DashboardResponsible {
    managerId?: string;
    managerName?: string;
    managerPhotoUrl?: string;
    committeesManaged: { committeeId: string; committeeName: string }[];
    committees: { label: string; total: number }[];
    ideas: { label: string; key: string; total: number }[];
    monthlyChart: {
        month: number;
        monthLabel: string;
        analysis: number;
        denied: number;
        approvedAward: number;
        awarded: number;
        total: number;
    }[];
}

export interface IdeaCommitteeDashboardResult {
    all: DashboardResponsible;
    responsibles: DashboardResponsible[];
}

export interface IdeaDashboardPage {
    results: {
        id: string;
        code?: string;
        title: string;
        responsible?: string;
        unity?: string;
    }[];
    totalCount: number;
    pageSize: number;
    currentPage: number;
    totalPage: number;
    previouPage: boolean; // 'previouPage' é typo do schema — manter exatamente assim
    nextPage: boolean;
}

const IDEA_COMMITTEE_DASHBOARD = gql`
    query IdeaCommitteeDashboard($data: SearchIdeaCommitteeDashboardInput!) {
        ideaCommitteeDashboard(data: $data) {
            all { ...resp }
            responsibles { ...resp }
        }
    }
    fragment resp on DashboardResponsibleDto {
        managerId
        managerName
        managerPhotoUrl
        committeesManaged { committeeId committeeName }
        committees { label total }
        ideas { label key total }
        monthlyChart { month monthLabel analysis denied approvedAward awarded total }
    }
`;

const IDEA_COMMITTEE_LIST_DASHBOARD = gql`
    query IdeaCommitteeListDashboard($data: SearchIdeaCommitteeDashboardInput!) {
        ideaCommitteeListDashboard(data: $data) {
            results { id code title responsible unity }
            totalCount
            pageSize
            currentPage
            totalPage
            previouPage
            nextPage
        }
    }
`;

// ---- Aba "Performance Geral" ----------------------------------------------
export interface SearchGeneralPerformanceDashboardInput {
    unityIds?: string[];
    committeeIds?: string[];
    challengeIds?: string[];
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    page?: number;
}

export interface GeneralPerformanceDashboardResult {
    ideasRegistered: number;
    awardedIdeasBrazil: number;
    awardedIdeasLatam: number;
    awardedCollaborators: number;
}

const GENERAL_PERFORMANCE_DASHBOARD = gql`
    query GeneralPerformanceDashboard($data: SearchGeneralPerformanceDashboardInput) {
        generalPerformanceDashboard(data: $data) {
            ideasRegistered
            awardedIdeasBrazil
            awardedIdeasLatam
            awardedCollaborators
        }
    }
`;

// totais de Ganhos + Investimento (só ideias premiadas, sem traduções; sem split Operações/Administrativo)
export interface GeneralPerformanceGainsResult {
    financialTotal: number;
    productivityHoursTotal: number;
    environmentalScoreTotal: number;
    investmentTotal: number;
}

const GENERAL_PERFORMANCE_GAINS_DASHBOARD = gql`
    query GeneralPerformanceGainsDashboard($data: SearchGeneralPerformanceDashboardInput) {
        generalPerformanceGainsDashboard(data: $data) {
            financialTotal
            productivityHoursTotal
            environmentalScoreTotal
            investmentTotal
        }
    }
`;

// por unidade
export interface GeneralPerformanceByUnityItem {
    unityName: string;
    ideasRegistered: number;
    awardedIdeas: number;
    awardedCollaborators: number;
}
export interface GeneralPerformanceByUnityResult {
    items: GeneralPerformanceByUnityItem[];
}
const GENERAL_PERFORMANCE_BY_UNITY = gql`
    query GeneralPerformanceByUnityDashboard($data: SearchGeneralPerformanceDashboardInput) {
        generalPerformanceByUnityDashboard(data: $data) {
            items { unityName ideasRegistered awardedIdeas awardedCollaborators }
        }
    }
`;

// mensal (Brasil e Latam compartilham o shape)
export interface IdeasMonthlyItem {
    year: number;
    month: number;
    monthLabel: string;
    unities: { unityName: string; total: number }[];
}
export interface IdeasByMonthResult {
    items: IdeasMonthlyItem[];
}
const BRAZIL_IDEAS_BY_UNITY_MONTHLY = gql`
    query BrazilIdeasByUnityMonthlyDashboard($data: SearchGeneralPerformanceDashboardInput) {
        brazilIdeasByUnityMonthlyDashboard(data: $data) {
            items { year month monthLabel unities { unityName total } }
        }
    }
`;
const LATAM_IDEAS_BY_UNITY_MONTHLY = gql`
    query LatamIdeasByUnityMonthlyDashboard($data: SearchGeneralPerformanceDashboardInput) {
        latamIdeasByUnityMonthlyDashboard(data: $data) {
            items { year month monthLabel unities { unityName total } }
        }
    }
`;

// lista de premiadas (paginada)
export interface AwardedIdeaListItem {
    ideaId: string;
    code?: string;
    title: string;
    committeeName?: string;
    unityName?: string;
    authorName?: string;
}
export interface AwardedIdeaListPaginator {
    results: AwardedIdeaListItem[];
    totalCount: number;
    pageSize: number;
    currentPage: number;
    totalPage: number;
    previouPage: boolean; // typo do schema — manter
    nextPage: boolean;
}
const AWARDED_IDEAS_LIST = gql`
    query AwardedIdeasListDashboard($data: SearchGeneralPerformanceDashboardInput) {
        awardedIdeasListDashboard(data: $data) {
            results { ideaId code title committeeName unityName authorName }
            totalCount
            pageSize
            currentPage
            totalPage
            previouPage
            nextPage
        }
    }
`;

@Injectable({ providedIn: 'root' })
export class DashboardApiService {

    constructor(private apollo: Apollo) {}

    ideaCommitteeDashboard(
        data: SearchIdeaCommitteeDashboardInput,
    ): Observable<ApolloQueryResult<{ ideaCommitteeDashboard: IdeaCommitteeDashboardResult }>> {
        return this.apollo.query<{ ideaCommitteeDashboard: IdeaCommitteeDashboardResult }>({
            query: IDEA_COMMITTEE_DASHBOARD,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    ideaCommitteeList(
        data: SearchIdeaCommitteeDashboardInput,
    ): Observable<ApolloQueryResult<{ ideaCommitteeListDashboard: IdeaDashboardPage }>> {
        return this.apollo.query<{ ideaCommitteeListDashboard: IdeaDashboardPage }>({
            query: IDEA_COMMITTEE_LIST_DASHBOARD,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    generalPerformance(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ generalPerformanceDashboard: GeneralPerformanceDashboardResult }>> {
        return this.apollo.query<{ generalPerformanceDashboard: GeneralPerformanceDashboardResult }>({
            query: GENERAL_PERFORMANCE_DASHBOARD,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    generalPerformanceGains(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ generalPerformanceGainsDashboard: GeneralPerformanceGainsResult }>> {
        return this.apollo.query<{ generalPerformanceGainsDashboard: GeneralPerformanceGainsResult }>({
            query: GENERAL_PERFORMANCE_GAINS_DASHBOARD,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    generalPerformanceByUnity(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ generalPerformanceByUnityDashboard: GeneralPerformanceByUnityResult }>> {
        return this.apollo.query<{ generalPerformanceByUnityDashboard: GeneralPerformanceByUnityResult }>({
            query: GENERAL_PERFORMANCE_BY_UNITY,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    brazilIdeasByUnityMonthly(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ brazilIdeasByUnityMonthlyDashboard: IdeasByMonthResult }>> {
        return this.apollo.query<{ brazilIdeasByUnityMonthlyDashboard: IdeasByMonthResult }>({
            query: BRAZIL_IDEAS_BY_UNITY_MONTHLY,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    latamIdeasByUnityMonthly(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ latamIdeasByUnityMonthlyDashboard: IdeasByMonthResult }>> {
        return this.apollo.query<{ latamIdeasByUnityMonthlyDashboard: IdeasByMonthResult }>({
            query: LATAM_IDEAS_BY_UNITY_MONTHLY,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }

    awardedIdeasList(
        data?: SearchGeneralPerformanceDashboardInput,
    ): Observable<ApolloQueryResult<{ awardedIdeasListDashboard: AwardedIdeaListPaginator }>> {
        return this.apollo.query<{ awardedIdeasListDashboard: AwardedIdeaListPaginator }>({
            query: AWARDED_IDEAS_LIST,
            variables: { data },
            fetchPolicy: 'no-cache',
        });
    }
}
